// Shared drill-down modal + hook. Any super-admin number can call
// openDrill(metric, params) and get the order list behind it, rendered
// from the column spec the endpoint declares, with CSV export. Used by
// Owner Cockpit, Money at Risk, and Payment Review.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cockpitApi, downloadAuthed } from '../api/services';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Loader2, Download } from 'lucide-react';

export function useDrill(asOf) {
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  async function openDrill(metric, params = {}) {
    setDrill({ metric, params });
    setDrillData(null);
    setDrillLoading(true);
    try {
      const res = await cockpitApi.drill({ metric, ...(asOf ? { date: asOf } : {}), ...params });
      setDrillData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load drill-down');
      setDrill(null);
    } finally { setDrillLoading(false); }
  }

  function exportDrill() {
    if (!drill) return;
    const qs = new URLSearchParams({
      metric: drill.metric, ...(asOf ? { date: asOf } : {}), ...drill.params, format: 'csv',
    }).toString();
    downloadAuthed(`/reports/cockpit/drill?${qs}`, `COCKPIT-${drill.metric}.csv`)
      .catch((err) => toast.error(err.response?.data?.error || 'Download failed'));
  }

  return {
    drill, drillData, drillLoading,
    openDrill, exportDrill,
    closeDrill: () => setDrill(null),
  };
}

export default function DrillModal({ data, loading, onClose, onExport }) {
  function cell(r, c) {
    const v = r[c.key];
    if (c.type === 'order') {
      return (
        <span className="whitespace-nowrap">
          <Link to={`/orders/${r.id}`} className="font-mono text-blue-600 hover:underline">{v}</Link>
          {r.is_reship && (
            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-violet-600 text-white text-[10px] font-semibold uppercase">
              Reship
            </span>
          )}
        </span>
      );
    }
    if (v == null || v === '') return <span className="text-gray-400">—</span>;
    if (c.type === 'days') {
      return (
        <span className={`font-semibold tabular-nums ${v > 14 ? 'text-rose-600' : v > 7 ? 'text-amber-700' : 'text-gray-900'}`}>
          {v}
        </span>
      );
    }
    if (c.type === 'datetime') {
      return <span className="whitespace-nowrap text-gray-600">{format(new Date(v), 'dd MMM yyyy HH:mm')}</span>;
    }
    if (c.type === 'date') {
      return <span className="whitespace-nowrap text-gray-600">{format(new Date(v), 'dd MMM yyyy')}</span>;
    }
    if (c.type === 'snake') return <span className="capitalize text-gray-600">{String(v).replace(/_/g, ' ')}</span>;
    return v;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{data?.title || 'Loading…'}</h2>
            <p className="text-xs text-gray-500">
              {data ? `${data.count} row(s) · as of ${data.asOf} · ` : ''}Click an order to see its full story.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data && data.count > 0 && (
              <button onClick={onExport}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            )}
            <button onClick={onClose}
                    className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
        <div className="overflow-y-auto">
          {loading && (
            <div className="py-12 text-center text-sm text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          )}
          {!loading && data && data.rows.length === 0 && (
            <div className="py-12 text-center text-sm text-gray-500">Nothing here — clean.</div>
          )}
          {!loading && data && data.rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 sticky top-0">
                <tr>
                  {data.columns.map((c) => (
                    <th key={c.key} className={`px-4 py-2 ${c.type === 'days' ? 'text-right' : 'text-left'}`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.rows.map((r, i) => (
                  <tr key={r.id || i} className="hover:bg-gray-50">
                    {data.columns.map((c) => (
                      <td key={c.key} className={`px-4 py-2 ${c.type === 'days' ? 'text-right' : ''}`}>{cell(r, c)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
